import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { XMarkIcon } from "@heroicons/react/24/outline";

interface QRCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialPlatform?: "ios" | "android";
}

const ANDROID_URL =
  "https://play.google.com/store/apps/details?id=com.vettconsult.mobile&pcampaignid=web_share";
const IOS_URL = "https://apps.apple.com/es/app/vettconsult/id6756122991";

const ANDROID_QR = "/images/qr/qr android.png";
const IOS_QR = "/images/qr/qr ios.png";

const QRCodeModal: React.FC<QRCodeModalProps> = ({
  isOpen,
  onClose,
  initialPlatform = "ios",
}) => {
  const { t } = useTranslation();
  const [activePlatform, setActivePlatform] = useState<"ios" | "android">(
    initialPlatform,
  );

  useEffect(() => {
    if (isOpen) {
      setActivePlatform(initialPlatform);
    }
  }, [isOpen, initialPlatform]);

  if (!isOpen) return null;

  const currentQrUrl = activePlatform === "ios" ? IOS_QR : ANDROID_QR;
  const currentStoreUrl = activePlatform === "ios" ? IOS_URL : ANDROID_URL;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200] p-4 pt-24">
      <div
        className="bg-white rounded-2xl shadow-xl max-w-sm w-full relative overflow-hidden transform transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-[#00342d] p-4 flex justify-between items-center text-white">
          <h3 className="font-bold text-lg">
            {t("appDownload.dashboardTitle", "Get the App")}
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/10 rounded-full transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 flex flex-col items-center">
          {/* Platform Tabs */}
          <div className="flex w-full mb-6 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActivePlatform("ios")}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                activePlatform === "ios"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              iOS
            </button>
            <button
              onClick={() => setActivePlatform("android")}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                activePlatform === "android"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Android
            </button>
          </div>

          {/* QR Code */}
          <div className="w-56 h-56 bg-white p-2 rounded-xl shadow-inner border border-gray-100 mb-4 p-4 flex items-center justify-center">
            <img
              src={currentQrUrl}
              alt={`${activePlatform} QR Code`}
              className="w-full h-full object-contain"
            />
          </div>

          <p className="text-center text-gray-600 text-sm mb-6">
            {t(
              "appDownload.modalInstruction",
              "Point your phone's camera at the QR code to download the VettConsult app, or use the direct links below.",
            )}
          </p>

          {/* Direct Download Link */}
          <a
            href={currentStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex flex-col items-center justify-center p-3 rounded-xl bg-vett-turquoise/10 hover:bg-vett-turquoise/20 text-vett-dark-green transition-colors border border-vett-turquoise/20 mb-4"
          >
            <div className="font-bold">
              {t("appDownload.downloadFor", "Download for")}{" "}
              {activePlatform === "ios" ? "iOS" : "Android"}
            </div>
            <span className="text-xs opacity-75">
              {activePlatform === "ios" ? "App Store" : "Google Play Store"}
            </span>
          </a>

          <button
            onClick={onClose}
            className="w-full py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium rounded-xl transition-colors text-sm"
          >
            {t("common.close", "Close")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QRCodeModal;
